import type { BlockDef } from "@cogo/types-v2"
import { materializeRenderPackTree } from "@cogo/runtime-v2"

export type AppRenderPackBytes = Record<string, Uint8Array>

export function materializeBlocksFromRenderPackBytes<T extends Record<string, BlockDef>>(
  blocks: T,
  renderPackBytes?: AppRenderPackBytes,
): T {
  if (!renderPackBytes || Object.keys(renderPackBytes).length === 0) return blocks

  let changed = false
  const materializedBlocks: Record<string, BlockDef> = { ...blocks }

  for (const [blockName, bytes] of Object.entries(renderPackBytes)) {
    const block = blocks[blockName]
    if (!block) continue

    const materialized = materializeRenderPackTree(bytes)
    const blockingDiagnostic = materialized.diagnostics.find((diagnostic) => diagnostic.severity === "error")
    if (blockingDiagnostic) {
      throw new Error(
        `RenderPack for block "${blockName}" failed materialization: ${blockingDiagnostic.code} ${blockingDiagnostic.message}`,
      )
    }
    if (!materialized.root) {
      throw new Error(`RenderPack for block "${blockName}" did not produce a root element.`)
    }

    materializedBlocks[blockName] = {
      ...block,
      ui: {
        ...(block.ui ?? {}),
        root: materialized.root as unknown as BlockDef["ui"]["root"],
      },
    }
    changed = true
  }

  return changed ? (materializedBlocks as T) : blocks
}
